import IJob from '@/models/jobs/IJob';

export interface IJobIndex {
    parentOf: Record<number, number>;
    childrenOf: Record<number, number[]>;
}

export function buildJobIndex(jobs: IJob[]): IJobIndex {
    const parentOf: Record<number, number> = {};
    const childrenOf: Record<number, number[]> = {};
    for (const j of jobs) {
        const parent = j.parentJobId || 0;
        parentOf[j.id] = parent;
        if (!childrenOf[parent]) {
            childrenOf[parent] = [];
        }
        childrenOf[parent].push(j.id);
    }
    return { parentOf, childrenOf };
}

/** The job itself plus every job below it, ascending. */
export function subtreeOf(jobId: number, idx: IJobIndex): number[] {
    const out: number[] = [];
    //  `seen` is a cycle guard, not an optimisation: the hierarchy is authored
    //  game data, so a self- or mutually-parented row would otherwise hang the
    //  page rather than render a slightly wrong tree.
    const seen = new Set<number>();
    const stack = [jobId];
    while (stack.length) {
        const id = stack.pop() as number;
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        out.push(id);
        for (const c of idx.childrenOf[id] || []) {
            stack.push(c);
        }
    }
    return out.sort((a, b) => a - b);
}

/** The picked jobs plus every ancestor of each, deduped and ascending. */
export function withAncestors(picked: number[], idx: IJobIndex): number[] {
    const out = new Set<number>(picked);
    for (const id of picked) {
        const seen = new Set<number>([id]);
        let p = idx.parentOf[id] || 0;
        while (p > 0 && !seen.has(p)) {
            seen.add(p);
            out.add(p);
            p = idx.parentOf[p] || 0;
        }
    }
    return [...out].sort((a, b) => a - b);
}
